import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { formatMoney, PAYMENT_LABEL } from "@/lib/api";
import { renderBrandedHeader, renderBrandedFooter, brandedTableHeadStyles, drawSignatureBlock, hexToRgb } from "@/lib/pdfBranding";
import { APPLIANCE_LABEL, PHASE_LABEL, CASE_STATUS, ORTHO_PHASES, INST_STATUS, fmtDate, monthsBetween } from "@/lib/ortho";

const C = { text: [26, 28, 26], muted: [110, 115, 110], border: [209, 209, 203], danger: [185, 28, 28] };
const M = 48;

function section(doc, title, y) {
  const W = doc.internal.pageSize.getWidth();
  y += 8;
  if (y > doc.internal.pageSize.getHeight() - 110) { doc.addPage(); y = M; }
  doc.setTextColor(27, 67, 50); doc.setFont("helvetica", "bold"); doc.setFontSize(13);
  doc.text(title, M, y);
  doc.setDrawColor(...C.border); doc.setLineWidth(0.5); doc.line(M, y + 4, W - M, y + 4);
  return y + 18;
}

function kvGrid(doc, fields, y, cols = 3) {
  const W = doc.internal.pageSize.getWidth();
  const colW = (W - 2 * M) / cols;
  let col = 0, rowY = y;
  doc.setFontSize(8.5);
  for (const [k, v] of fields) {
    const x = M + col * colW;
    doc.setFont("helvetica", "normal"); doc.setTextColor(...C.muted); doc.text(k.toUpperCase(), x, rowY);
    doc.setFont("helvetica", "bold"); doc.setTextColor(...C.text);
    doc.text(doc.splitTextToSize(String(v ?? "—"), colW - 10), x, rowY + 12);
    col += 1;
    if (col >= cols) { col = 0; rowY += 30; }
  }
  return rowY + (col === 0 ? 0 : 30);
}

function longText(doc, label, text, y) {
  if (!text) return y;
  const W = doc.internal.pageSize.getWidth();
  if (y > doc.internal.pageSize.getHeight() - 90) { doc.addPage(); y = M; }
  doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(...C.muted); doc.text(label.toUpperCase(), M, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9.5); doc.setTextColor(...C.text);
  const lines = doc.splitTextToSize(text, W - 2 * M);
  doc.text(lines, M, y + 13);
  return y + 13 + lines.length * 12 + 6;
}

export function generateOrthoPdf({ patient, caseData, controls = [], installments = [], branding, doctor }) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();
  const primary = hexToRgb(branding?.color_primario || "#1B4332");
  let y = renderBrandedHeader(doc, branding, { title: "REPORTE DE ORTODONCIA", subtitle: "Seguimiento del tratamiento y estado de pagos" });

  // Paciente
  y = section(doc, "Paciente", y);
  y = kvGrid(doc, [
    ["Nombre", patient?.name],
    ["Documento", patient?.document_number ? `${patient.document_type || ""} ${patient.document_number}`.trim() : "—"],
    ["Teléfono", patient?.phone || "—"],
  ], y);

  // Caso
  const elapsed = monthsBetween(caseData.start_date);
  const pct = caseData.estimated_months ? Math.min(100, Math.round((elapsed / caseData.estimated_months) * 100)) : 0;
  y = section(doc, "Caso de ortodoncia", y);
  y = kvGrid(doc, [
    ["Aparatología", APPLIANCE_LABEL[caseData.appliance_type] || caseData.appliance_type],
    ["Estado", CASE_STATUS[caseData.status] || caseData.status],
    ["Ortodoncista", caseData.doctor_name || doctor?.name || "—"],
    ["Fecha de inicio", fmtDate(caseData.start_date)],
    ["Duración estimada", `${caseData.estimated_months} meses`],
    ["Avance", `Mes ${elapsed} de ${caseData.estimated_months} (${pct}%)`],
    ["Fase actual", PHASE_LABEL[caseData.phase] || caseData.phase],
    ["Arco superior", caseData.upper_arch || "—"],
    ["Arco inferior", caseData.lower_arch || "—"],
    ["Elásticos", caseData.elastics || "—"],
    ["Último control", caseData.last_control_date ? fmtDate(caseData.last_control_date) : "—"],
    ["Próximo control", caseData.next_visit ? fmtDate(caseData.next_visit) : "Sin programar"],
  ], y);

  // Barra de avance
  doc.setFillColor(230, 230, 226); doc.roundedRect(M, y, W - 2 * M, 8, 3, 3, "F");
  doc.setFillColor(...primary); doc.roundedRect(M, y, Math.max(8, (W - 2 * M) * pct / 100), 8, 3, 3, "F");
  y += 16;
  // Fases
  const phaseIdx = ORTHO_PHASES.findIndex((p) => p.key === caseData.phase);
  let px = M;
  doc.setFontSize(7.5);
  ORTHO_PHASES.forEach((p, i) => {
    const w = doc.getTextWidth(p.label.toUpperCase()) + 12;
    if (i <= phaseIdx) { doc.setFillColor(...primary); doc.setTextColor(255, 255, 255); }
    else { doc.setFillColor(240, 240, 236); doc.setTextColor(...C.muted); }
    doc.roundedRect(px, y, w, 14, 2, 2, "F");
    doc.setFont("helvetica", "bold"); doc.text(p.label.toUpperCase(), px + 6, y + 10);
    px += w + 6;
  });
  y += 28;

  y = longText(doc, "Diagnóstico", caseData.diagnosis, y);
  y = longText(doc, "Objetivos del tratamiento", caseData.objectives, y);
  y = longText(doc, "Notas", caseData.notes, y);

  // Controles (cronológico)
  y = section(doc, `Línea de tiempo de controles (${controls.length})`, y);
  if (controls.length === 0) {
    doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(...C.muted);
    doc.text("Aún no hay controles registrados.", M, y); y += 16;
  } else {
    const ordered = [...controls].sort((a, b) => (a.date < b.date ? -1 : 1));
    autoTable(doc, {
      startY: y + 2,
      head: [["#", "Fecha", "Fase", "Arco sup.", "Arco inf.", "Ligaduras", "Elásticos", "Procedimientos / observaciones", "Próximo"]],
      body: ordered.map((c, i) => [
        String(i + 1), fmtDate(c.date), c.phase ? PHASE_LABEL[c.phase] : "—", c.upper_arch || "—", c.lower_arch || "—",
        c.ligatures_color || "—", c.elastics || "—",
        [c.procedures, c.observations].filter(Boolean).join("\n") || "Control de rutina",
        c.next_visit ? fmtDate(c.next_visit) : "—",
      ]),
      theme: "striped",
      headStyles: { ...brandedTableHeadStyles(branding), fontSize: 7.5 },
      bodyStyles: { fontSize: 7.5, textColor: C.text, valign: "top" },
      columnStyles: { 0: { cellWidth: 18 }, 1: { cellWidth: 62 }, 7: { cellWidth: 150 } },
      margin: { left: M, right: M },
    });
    y = doc.lastAutoTable.finalY + 14;
  }

  // Pagos
  const paid = installments.filter((i) => i.status === "pagado").reduce((s, i) => s + (i.paid_amount ?? i.amount), 0);
  const total = installments.reduce((s, i) => s + i.amount, 0);
  const overdue = installments.filter((i) => i.status === "vencido");
  y = section(doc, "Estado de pagos", y);
  if (installments.length === 0) {
    doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(...C.muted);
    doc.text("No se ha configurado un plan de cuotas.", M, y); y += 16;
  } else {
    y = kvGrid(doc, [
      ["Total del plan", formatMoney(total)],
      ["Pagado", formatMoney(paid)],
      ["Saldo pendiente", formatMoney(total - paid)],
      ["Cuotas pagadas", `${installments.filter((i) => i.status === "pagado").length} de ${installments.length}`],
      ["Cuotas vencidas", overdue.length ? `${overdue.length} · ${formatMoney(overdue.reduce((s, i) => s + i.amount, 0))}` : "Ninguna"],
      ["Mensualidad", caseData.payment_plan?.monthly_fee ? formatMoney(caseData.payment_plan.monthly_fee) : "—"],
    ], y);
    autoTable(doc, {
      startY: y + 2,
      head: [["Cuota", "Vencimiento", "Valor", "Estado", "Fecha de pago", "Medio de pago"]],
      body: installments.map((i) => [
        i.label, fmtDate(i.due_date), formatMoney(i.amount), (INST_STATUS[i.status] || INST_STATUS.pendiente).label,
        i.paid_at ? fmtDate(i.paid_at) : "—", i.payment_method ? (PAYMENT_LABEL[i.payment_method] || i.payment_method) : "—",
      ]),
      theme: "striped",
      headStyles: { ...brandedTableHeadStyles(branding), fontSize: 8.5 },
      bodyStyles: { fontSize: 8.5, textColor: C.text },
      columnStyles: { 2: { halign: "right", fontStyle: "bold" } },
      didParseCell: (d) => {
        if (d.section === "body" && d.column.index === 3) {
          const st = installments[d.row.index]?.status;
          if (st === "vencido") { d.cell.styles.textColor = C.danger; d.cell.styles.fontStyle = "bold"; }
          if (st === "pagado") { d.cell.styles.textColor = primary; d.cell.styles.fontStyle = "bold"; }
        }
      },
      margin: { left: M, right: M },
    });
    y = doc.lastAutoTable.finalY + 14;
  }

  // Firmas
  if (y > doc.internal.pageSize.getHeight() - 130) { doc.addPage(); y = M; }
  const sigW = (W - 2 * M - 40) / 2;
  const baseline = y + 60;
  drawSignatureBlock(doc, { x: M, baselineY: baseline, width: sigW, signatureImage: doctor?.signature_image, label: "Ortodoncista", name: caseData.doctor_name || doctor?.name || "", subtitle: doctor?.signature_text || doctor?.registry_number || "" });
  drawSignatureBlock(doc, { x: M + sigW + 40, baselineY: baseline, width: sigW, label: "Paciente / acudiente", name: patient?.name || "", subtitle: patient?.document_number ? `${patient.document_type || ""} ${patient.document_number}`.trim() : "" });

  const pages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    renderBrandedFooter(doc, branding);
    doc.setFontSize(8); doc.setTextColor(...C.muted);
    doc.text(`Página ${p} de ${pages}`, W - M, doc.internal.pageSize.getHeight() - 24, { align: "right" });
  }
  const safeName = (patient?.name || "paciente").replace(/\s+/g, "_");
  doc.save(`ortodoncia_${safeName}_${new Date().toISOString().slice(0, 10)}.pdf`);
}
